import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Switch,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  InputAdornment,
  Alert,
  Snackbar,
  Autocomplete
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SubCategoryForm from '../../components/Category/SubCategoryForm';
import DeleteConfirmDialog from '../../components/Common/DeleteConfirmDialog';
import { subCategoryService } from '../../services/subCategoryService';
import { webCategoryService } from '../../services/webCategoryService';
import useResponsive from '../../hooks/useResponsive';
import PageHeaderLayout from '../../components/Layout/PageHeaderLayout';

const SubCategoryList = () => {
  const isMobile = useResponsive('down', 'sm');
  const menuDispatch = useDispatch();
  const navigate = useNavigate();

  // Set active menu on component mount
  useEffect(() => {
    menuDispatch({
      type: 'menuSelect',
      payload: 'web-subcategory'
    });
  }, []);

  // State management
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterActive, setFilterActive] = useState(null);
  const [filterCategoryId, setFilterCategoryId] = useState(null);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loadingServiceCategories, setLoadingServiceCategories] = useState(false);
  
  // Dialog states
  const [openForm, setOpenForm] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [formMode, setFormMode] = useState('create');
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch service categories for filter dropdown
  useEffect(() => {
    const fetchServiceCategories = async () => {
      try {
        setLoadingServiceCategories(true);
        const response = await webCategoryService.getParentCategoriesForDropdown({ limit: 200, isActive: true });
        if (response.success) {
          setServiceCategories(response.data || []);
        }
      } catch (error) {
        console.error('Error fetching service categories:', error);
      } finally {
        setLoadingServiceCategories(false);
      }
    };
    fetchServiceCategories();
  }, []);

  // Fetch categories for table
  const fetchSubCategories = async () => {
    try {
      setLoading(true);
      const params = {
        limit: 100,
        ...(filterActive !== null && { isActive: filterActive }),
        ...(filterCategoryId !== null && { categoryId: filterCategoryId })
      };

      const response = await webCategoryService.getAllCategories(params);
      
      if (response.success) {
        let data = response.data;
        
        // Apply search filter
        if (searchQuery) {
          data = data.filter(cat => 
            (cat.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (cat.slug || '').toLowerCase().includes(searchQuery.toLowerCase())
          );
        }
        
        // Pagination
        const startIndex = paginationModel.page * paginationModel.pageSize;
        const endIndex = startIndex + paginationModel.pageSize;
        const paginatedData = data.slice(startIndex, endIndex);
        
        setSubCategories(paginatedData);
        setTotalRows(data.length);
      }
    } catch (error) {
      showSnackbar('Error fetching categories', 'error');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubCategories();
  }, [paginationModel, searchQuery, filterActive, filterCategoryId]);

  // Handlers
  const handleCreate = () => {
    setFormMode('create');
    setSelectedSubCategory(null);
    setOpenForm(true);
  };

  const handleEdit = (subCategory) => {
    setFormMode('edit');
    setSelectedSubCategory(subCategory);
    setOpenForm(true);
  };

  const handleDelete = (subCategory) => {
    setSelectedSubCategory(subCategory);
    setOpenDelete(true);
  };

  const handleFormClose = () => {
    setOpenForm(false);
    setSelectedSubCategory(null);
  };

  const handleFormSuccess = () => {
    handleFormClose();
    fetchSubCategories();
    showSnackbar(
      `Category ${formMode === 'create' ? 'created' : 'updated'} successfully`,
      'success'
    );
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await webCategoryService.deleteCategory(selectedSubCategory._id);
      if (response.success) {
        setOpenDelete(false);
        fetchSubCategories();
        showSnackbar('Category deleted successfully', 'success');
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error deleting category', 'error');
    }
  };

  const handleToggleStatus = async (subCategory) => {
    try {
      const response = await webCategoryService.updateCategory(subCategory._id, {
        ...subCategory,
        isActive: !subCategory.isActive
      });
      
      if (response.success) {
        fetchSubCategories();
        showSnackbar('Status updated successfully', 'success');
      }
    } catch (error) {
      showSnackbar('Error updating status', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // DataGrid columns
  const columns = [
    {
      field: 'heading',
      headerName: 'Title',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => params.row.heading || '-'
    },
    {
      field: 'slug',
      headerName: 'Slug',
      flex: 1,
      minWidth: 150,
      hide: isMobile
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 200,
      hide: isMobile
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Switch
          checked={params.value}
          onChange={() => handleToggleStatus(params.row)}
          color="primary"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            color="primary"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row)}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <PageHeaderLayout headerTitle="Web Categories">
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={{ minWidth: { xs: 'auto', sm: 140 } }}
        >
          {isMobile ? 'Add' : 'Add Category'}
        </Button>
      </PageHeaderLayout>
      
      <Paper sx={{ p: 2, mt: 2 }}>
        {/* Filters */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
          <TextField
            size="small"
            placeholder="Search categories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 200 }}
          />

          <Autocomplete
            size="small"
            options={serviceCategories}
            getOptionLabel={(opt) => opt.title || opt.name || ''}
            loading={loadingServiceCategories}
            value={serviceCategories.find(c => c.id === filterCategoryId) || null}
            onChange={(_, newVal) => {
              setFilterCategoryId(newVal ? newVal.id : null);
              setPaginationModel(m => ({ ...m, page: 0 }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by Service Category"
                placeholder="All categories"
              />
            )}
            sx={{ minWidth: 220 }}
          />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label="All"
              onClick={() => setFilterActive(null)}
              color={filterActive === null ? 'primary' : 'default'}
              variant={filterActive === null ? 'filled' : 'outlined'}
            />
            <Chip
              label="Active"
              onClick={() => setFilterActive(true)}
              color={filterActive === true ? 'success' : 'default'}
              variant={filterActive === true ? 'filled' : 'outlined'}
            />
            <Chip
              label="Inactive"
              onClick={() => setFilterActive(false)}
              color={filterActive === false ? 'error' : 'default'}
              variant={filterActive === false ? 'filled' : 'outlined'}
            />
          </Box>
        </Box>

        {/* Data Grid */}
        <DataGrid
          rows={subCategories}
          columns={columns}
          getRowId={(row) => row._id}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={totalRows}
          loading={loading}
          pageSizeOptions={[5, 10, 25, 50]}
          disableRowSelectionOnClick
          autoHeight
          sx={{
            '& .MuiDataGrid-cell:focus': {
              outline: 'none'
            }
          }}
        />
      </Paper>

      {/* Form Dialog */}
      <Dialog 
        open={openForm} 
        onClose={handleFormClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100%', sm: '90vh' },
            width: { xs: '100%', sm: '90%', md: '80%', lg: '70%' }
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          fontWeight: 600
        }}>
          {formMode === 'create' ? 'Add New Category' : 'Edit Category'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <SubCategoryForm
            subCategory={selectedSubCategory}
            mode={formMode}
            onSuccess={handleFormSuccess}
            onCancel={handleFormClose}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={openDelete}
        title="Delete Category"
        message={`Are you sure you want to delete "${selectedSubCategory?.name}"? This will also delete all subcategories.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setOpenDelete(false)}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SubCategoryList;
